import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GroupService } from '../../../core/services/group.service';
import { isStateModeGroup } from '../../../shared/util/state-mode.util';
import * as _ from 'lodash';
import * as moment from 'moment';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { ResultsService } from './../../../core/services/results.service';
import { PatientService } from './../../../core/services/patient.service';
import { PatientLabService } from './../../../core/services/patient-lab.service';
import { LANCET_LOGO } from './../../../core/data/app.data';
import { LabResultsDialogComponent, LabResultsDialogData } from './../../../shared/components/lab-results-dialog/lab-results-dialog.component';
import {
    isUnlinkedConflict,
    unlinkedBlobErrorMessage,
    unlinkedErrorMessage,
    unlinkedPdfFileName,
    UnlinkedResultDto
} from './../../../core/models/unlinked-result.model';
import {
    matchesVendorDoctorRoles,
    VendorDoctorBadge,
    VendorDoctorBadgeCache,
    VendorDoctorBearing,
    vendorDoctorRoleOptions
} from './../../../core/models/vendor-doctors.model';
import { FileDownloadService } from './../../../core/services/file-download.service';
import {
    UnlinkedResultLinkDialogComponent,
    UnlinkedResultLinkDialogData,
    UnlinkedResultLinkDialogResult
} from './../../../shared/components/unlinked-result-link-dialog/unlinked-result-link-dialog.component';
import {
    UnlinkedResultDismissDialogComponent,
    UnlinkedResultDismissDialogData
} from './../../../shared/components/unlinked-result-dismiss-dialog/unlinked-result-dismiss-dialog.component';
import {
    UnlinkedResultPayloadDialogComponent,
    UnlinkedResultPayloadDialogData
} from './../../../shared/components/unlinked-result-payload-dialog/unlinked-result-payload-dialog.component';

@Component({
    selector: 'app-results-list',
    templateUrl: './results-list.component.html',
    styleUrls: ['./results-list.component.scss']
})
export class ResultsListComponent extends BaseComponent implements OnInit, OnChanges {
    filterValue: string = '';
    filteredResults: any[] = [];
    filteredFailed: any[] = [];
    filteredPending: any[] = [];
    filteredFlagged: any[] = [];
    filteredUnlinked: any[] = [];
    allResults: any[] = [];
    allUnlinked: UnlinkedResultDto[] = [];

    // Pre-computed counts
    resultsCount: number = 0;
    failedCount: number = 0;
    pendingCount: number = 0;
    flaggedCount: number = 0;
    unlinkedCount: number = 0;

    selectedTabIndex: number = 0;

    // -----------------------------------------------------------------------
    // Vendor doctor badges and the role filter
    // -----------------------------------------------------------------------

    /**
     * OUR OWN vendor code, off the list envelopes. `null` until a response has arrived, and
     * while it is null NOTHING is suppressed — see `vendor-doctors.model.ts`. Never a literal.
     */
    vendorDoctorPlatformCode: string | null = null;

    /**
     * The distinct role codes present on the rows that survive the TEXT filter, across every
     * tab. Empty means no loaded row names a role, and the filter control is not rendered at
     * all — which is the state of this database today, and an always-empty menu is worse than
     * no menu.
     */
    vendorDoctorRoleOptions: string[] = [];

    /** Ticked roles. Multi-select is OR; empty means no role filtering. */
    selectedVendorDoctorRoles: string[] = [];

    /** Per-row memo so `*ngFor` does not rebuild every chip on every change-detection pass. */
    private badgeCache = new VendorDoctorBadgeCache();

    /** Ids with a link/dismiss request in flight — keeps a double click from double posting. */
    busyUnlinkedIds = new Set<string>();

    /** Ids with a PDF download in flight. Separate from busyUnlinkedIds: a download writes nothing,
     *  so it must not block Link/Dismiss, and Link/Dismiss must not block a download. */
    downloadingUnlinkedIds = new Set<string>();

    /** Ids whose parsed document is being fetched for the read-only viewer. */
    openingUnlinkedIds = new Set<string>();

    // Lab provider logos
    lancetLogo = LANCET_LOGO;

    /** A state unit (tasks/state-mode.md) orders nothing from a lab, so lab orders are not edited. */
    get stateUnit(): boolean {
        return isStateModeGroup(this.groupService.group);
    }

    constructor(
        private patientService: PatientService,
        private patientLabService: PatientLabService,
        private resultsService: ResultsService,
        private fileDownloadService: FileDownloadService,
        private router: Router,
        private groupService: GroupService,
        fs: FeedbackService,
        md: MatDialog
    ) {
        super(fs, md);
    }

    ngOnInit() {
        this.resultsService.results$
            .pipe(takeUntil(this.destroy$))
            .subscribe((results: any) => {
                this.allResults = results;
                this.updateFilteredLists();
            });

        this.resultsService.unlinked$
            .pipe(takeUntil(this.destroy$))
            .subscribe((unlinked: UnlinkedResultDto[]) => {
                this.allUnlinked = unlinked ?? [];
                this.updateFilteredLists();
            });

        this.resultsService.vendorDoctorPlatformCode$
            .pipe(takeUntil(this.destroy$))
            .subscribe((code: string | null) => {
                this.vendorDoctorPlatformCode = code;
                // Every cached badge set was built against the OLD code, so every suppression
                // decision in it is now unverified.
                this.badgeCache.clear();
                this.updateFilteredLists();
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['filterValue']) {
            this.updateFilteredLists();
        }
    }

    applyFilter(event?: Event) {
        if (event) {
            this.filterValue = (event.target as HTMLInputElement).value;
        }
        this.updateFilteredLists();
    }

    updateFilteredLists() {
        // Precompute all categorized orders with display data
        const results = this.allResults.filter(order =>
            (order.status === 'completed' || order.status === 'cancelled') &&
            !order.reviewedAt
        ).map(order => this.precomputeDisplayData(order));

        const failed = this.allResults.filter(order => order.status === 'failed')
            .map(order => this.precomputeDisplayData(order));

        const pending = this.allResults.filter(order =>
            ['pending', 'ordered', 'in_progress'].includes(order.status)
        ).map(order => this.precomputeDisplayData(order));

        // Flagged orders - include ALL flagged orders regardless of review status
        // This ensures flagged items remain visible even after being marked as reviewed
        const flagged = this.allResults.filter(order => order.flagged === true)
            .map(order => this.precomputeDisplayData(order));

        // Update counts
        this.resultsCount = results.length;
        this.failedCount = failed.length;
        this.pendingCount = pending.length;
        this.flaggedCount = flagged.length;

        // Unlinked results carry vendor demographics rather than a Patient record,
        // so they need their own filter and their own date key.
        this.unlinkedCount = this.allUnlinked.length;

        // ── The two filters COMPOSE. Text first, roles second, both must pass (AND). ──────
        //
        // The role options are derived BETWEEN the two, from the rows the text search left
        // standing: a role that exists only on a row the search has already excluded must not
        // be offered as a choice, or the menu offers something that can only ever empty the
        // list. They are deliberately NOT derived after the role filter — that would make each
        // option vanish the moment it was ticked.
        const textResults = this.applyTextFilter(results);
        const textFailed = this.applyTextFilter(failed);
        const textPending = this.applyTextFilter(pending);
        const textFlagged = this.applyTextFilter(flagged);
        const textUnlinked = this.applyUnlinkedTextFilter(this.allUnlinked);

        this.vendorDoctorRoleOptions = vendorDoctorRoleOptions(
            [...textResults, ...textFailed, ...textPending, ...textFlagged, ...textUnlinked],
            this.vendorDoctorPlatformCode
        );

        this.filteredResults = this.groupAndSort(this.applyRoleFilter(textResults));
        this.filteredFailed = this.groupAndSort(this.applyRoleFilter(textFailed));
        this.filteredPending = this.groupAndSort(this.applyRoleFilter(textPending));
        this.filteredFlagged = this.groupAndSort(this.applyRoleFilter(textFlagged));
        this.filteredUnlinked = this.groupAndSortUnlinked(this.applyRoleFilter(textUnlinked));
    }

    // -----------------------------------------------------------------------
    // Role filter — the interaction is the clinical-timeline one: a filter_list icon button
    // opening a mat-menu of mat-checkboxes, `(change)` re-applying, and stopPropagation on the
    // items so ticking a second role does not close the menu.
    // -----------------------------------------------------------------------

    /**
     * ANDed with the text filter, never substituted for it. Nothing ticked passes everything
     * through — including the rows with no `vendorDoctors` at all, which today is all of them.
     */
    private applyRoleFilter<T extends VendorDoctorBearing>(items: T[]): T[] {
        if (!this.selectedVendorDoctorRoles.length) {
            return items;
        }
        return items.filter(item =>
            matchesVendorDoctorRoles(item, this.selectedVendorDoctorRoles, this.vendorDoctorPlatformCode));
    }

    /** The chips for one row, memoised. Empty array => the template renders no container. */
    vendorDoctorBadgesFor(row: VendorDoctorBearing | null | undefined): ReadonlyArray<VendorDoctorBadge> {
        return this.badgeCache.badgesFor(row, this.vendorDoctorPlatformCode);
    }

    isVendorDoctorRoleSelected(role: string): boolean {
        return this.selectedVendorDoctorRoles.includes(role);
    }

    toggleVendorDoctorRole(role: string, checked: boolean): void {
        const without = this.selectedVendorDoctorRoles.filter(selected => selected !== role);
        this.selectedVendorDoctorRoles = checked ? [...without, role] : without;
        this.updateFilteredLists();
    }

    clearVendorDoctorRoles(): void {
        if (!this.selectedVendorDoctorRoles.length) {
            return;
        }
        this.selectedVendorDoctorRoles = [];
        this.updateFilteredLists();
    }

    /**
     * Single text filter shared by every lab-order tab.
     * An empty search box passes the list through untouched, which is what the four
     * duplicated per-tab blocks used to do.
     */
    private applyTextFilter(items: any[]): any[] {
        if (this.filterValue.trim() === '') {
            return items;
        }
        const filterTerms = this.filterValue.toLowerCase().split(' ');
        return items.filter(item => this.matchesFilterTerms(item, filterTerms));
    }

    /** Every search term must hit at least one patient field, or every term must hit one ICD code. */
    private matchesFilterTerms(item: any, filterTerms: string[]): boolean {
        const icdCodesMatch = item.Patient?.icdCodes?.some((icdCode: any) =>
            filterTerms.every(term =>
                _.includes(icdCode.code?.toLowerCase(), term) ||
                _.includes(icdCode.description?.toLowerCase(), term)
            )
        );

        const patientMatch = filterTerms.every(term =>
            _.includes(item.Patient?.name?.toLowerCase() || '', term) ||
            _.includes(item.Patient?.surname?.toLowerCase() || '', term) ||
            _.includes(item.Patient?.patientNumber?.toLowerCase() || '', term) ||
            _.includes(item.Patient?.diagnosis?.toLowerCase() || '', term) ||
            _.includes(item.testNames?.toLowerCase() || '', term)
        );

        return Boolean(icdCodesMatch || patientMatch);
    }

    // -----------------------------------------------------------------------
    // Unlinked results
    // -----------------------------------------------------------------------

    private applyUnlinkedTextFilter(items: UnlinkedResultDto[]): UnlinkedResultDto[] {
        if (this.filterValue.trim() === '') {
            return items;
        }
        const filterTerms = this.filterValue.toLowerCase().split(' ');
        return items.filter(item => filterTerms.every(term =>
            _.includes(item.demographics?.surname?.toLowerCase() || '', term) ||
            _.includes(item.demographics?.firstName?.toLowerCase() || '', term) ||
            _.includes(item.demographics?.idNumber?.toLowerCase() || '', term) ||
            _.includes(item.demographics?.passportNumber?.toLowerCase() || '', term) ||
            _.includes(item.doctorName?.toLowerCase() || '', term) ||
            _.includes(item.orderingDoctorCode?.toLowerCase() || '', term) ||
            _.includes(item.specimenNumber?.toLowerCase() || '', term) ||
            _.includes(item.reportType?.toLowerCase() || '', term)
        ));
    }

    private groupAndSortUnlinked(data: UnlinkedResultDto[]) {
        const grouped = _.groupBy(data, item =>
            item.receivedAt && moment(item.receivedAt).isValid() ? moment(item.receivedAt).format('YYYY-MM-DD') : ''
        );

        return _.chain(grouped)
            .map((value, key) => ({
                date: key,
                items: _.orderBy(
                    value,
                    [
                        (item: UnlinkedResultDto) => (item.demographics?.surname || '').toLowerCase(),
                        (item: UnlinkedResultDto) => (item.demographics?.firstName || '').toLowerCase()
                    ],
                    ['asc', 'asc']
                )
            }))
            .orderBy(group => group.date, 'desc')
            .value();
    }

    /** "Surname, First name" as sent by the lab — this is not a patient record yet. */
    unlinkedPatientName(row: UnlinkedResultDto): string {
        const surname = (row?.demographics?.surname || '').trim();
        const firstName = (row?.demographics?.firstName || '').trim();
        const joined = [surname, firstName].filter(Boolean).join(', ');
        return joined || 'Unknown patient';
    }

    unlinkedDoctor(row: UnlinkedResultDto): string {
        const name = (row?.doctorName || '').trim();
        const code = (row?.orderingDoctorCode || '').trim();
        if (name && code) {
            return `${name} (${code})`;
        }
        return name || code || 'Unknown doctor';
    }

    isUnlinkedBusy(row: UnlinkedResultDto): boolean {
        return this.busyUnlinkedIds.has(row?.id);
    }

    isUnlinkedDownloading(row: UnlinkedResultDto): boolean {
        return this.downloadingUnlinkedIds.has(row?.id);
    }

    isUnlinkedOpening(row: UnlinkedResultDto): boolean {
        return this.openingUnlinkedIds.has(row?.id);
    }

    private unlinkedGroupId(row: UnlinkedResultDto): string | null {
        return row?.groupId || this.resultsService.groupId || null;
    }

    /**
     * Opens the ranked-candidate dialog, then confirms, then links.
     * Two explicit human actions are required before anything is written.
     */
    linkUnlinkedResult(event: Event, row: UnlinkedResultDto): void {
        event.stopPropagation();
        if (this.isUnlinkedBusy(row)) {
            return;
        }
        const groupId = this.unlinkedGroupId(row);
        if (!groupId) {
            this.showToastMessage('No group selected.');
            return;
        }

        const dialogData: UnlinkedResultLinkDialogData = { unlinked: row };
        const dialogRef = this.dialog.open(UnlinkedResultLinkDialogComponent, {
            width: '680px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            autoFocus: false,
            data: dialogData
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(async (choice: UnlinkedResultLinkDialogResult | undefined) => {
                if (!choice?.patientId) {
                    return;
                }
                const confirmed = await this.showConfirmationDialog(
                    'Link result to patient?',
                    `Link the ${row.reportType || 'result'} for ${this.unlinkedPatientName(row)} `
                    + `(specimen ${row.specimenNumber || 'unknown'}) to ${choice.patientLabel}? `
                    + `It becomes part of that patient file.`,
                    undefined,
                    'Cancel',
                    'Link',
                    false
                );
                if (!confirmed) {
                    return;
                }

                this.busyUnlinkedIds.add(row.id);
                this.resultsService.linkUnlinkedResult(groupId, row.id, choice.patientId)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (res) => {
                            this.busyUnlinkedIds.delete(row.id);
                            if (!res?.success) {
                                this.showToastMessage(res?.message || 'Could not link the result.');
                                return;
                            }
                            // The endpoint reports the row's ACTUAL state: a row the server had
                            // already auto-linked comes back 'auto_linked', not 'manually_linked'.
                            if (res.data?.resolutionState === 'auto_linked') {
                                this.showToastMessage(
                                    `This result was already linked automatically to ${choice.patientLabel}.`
                                );
                            } else {
                                this.showToastMessage(`Result linked to ${choice.patientLabel}.`);
                            }
                        },
                        error: (err: any) => {
                            this.busyUnlinkedIds.delete(row.id);
                            this.handleUnlinkedError(err, 'Could not link the result.');
                        }
                    });
            });
    }

    /** Dismissing needs a reason — the server rejects the call without one. */
    dismissUnlinkedResult(event: Event, row: UnlinkedResultDto): void {
        event.stopPropagation();
        if (this.isUnlinkedBusy(row)) {
            return;
        }
        const groupId = this.unlinkedGroupId(row);
        if (!groupId) {
            this.showToastMessage('No group selected.');
            return;
        }

        const dialogData: UnlinkedResultDismissDialogData = {
            patientLabel: this.unlinkedPatientName(row),
            specimenNumber: row.specimenNumber
        };
        const dialogRef = this.dialog.open(UnlinkedResultDismissDialogComponent, {
            width: '440px',
            maxWidth: '95vw',
            autoFocus: false,
            data: dialogData
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((reason: string | undefined) => {
                if (!reason) {
                    return;
                }
                this.busyUnlinkedIds.add(row.id);
                this.resultsService.dismissUnlinkedResult(groupId, row.id, reason)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (res) => {
                            this.busyUnlinkedIds.delete(row.id);
                            if (res?.success) {
                                this.showToastMessage('Result dismissed.');
                            } else {
                                this.showToastMessage(res?.message || 'Could not dismiss the result.');
                            }
                        },
                        error: (err: any) => {
                            this.busyUnlinkedIds.delete(row.id);
                            this.handleUnlinkedError(err, 'Could not dismiss the result.');
                        }
                    });
            });
    }

    /**
     * Opens the clinician-facing view of an unlinked result, READ ONLY.
     *
     * The parsed document is fetched first so the dialog never has to ask for a lab order that
     * does not exist. `readOnly` turns off every patient-bound and outbound capability in the
     * dialog — see `labResultsDialogCapabilities`.
     */
    viewUnlinkedResults(event: Event, row: UnlinkedResultDto): void {
        event.stopPropagation();
        if (this.isUnlinkedOpening(row)) {
            return;
        }
        const groupId = this.unlinkedGroupId(row);
        if (!groupId) {
            this.showToastMessage('No group selected.');
            return;
        }

        this.openingUnlinkedIds.add(row.id);
        this.resultsService.getUnlinkedResultDetail(groupId, row.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    this.openingUnlinkedIds.delete(row.id);
                    if (!res?.success || !res?.data) {
                        this.showToastMessage(res?.message || 'Could not load the result.');
                        return;
                    }
                    const dialogData: LabResultsDialogData = {
                        readOnly: true,
                        groupId,
                        unlinkedResult: res.data,
                        // The detail endpoint returns the parsed DOCUMENT and knows nothing
                        // about the parties, so the badges come from the worklist row that was
                        // clicked. Passing them changes nothing about the read-only gating —
                        // `readOnly` still drives `labResultsDialogCapabilities` on its own.
                        vendorDoctors: row.vendorDoctors ?? null,
                        vendorDoctorPlatformCode: this.vendorDoctorPlatformCode
                    };
                    this.dialog.open(LabResultsDialogComponent, {
                        data: dialogData,
                        width: '90vw',
                        height: '90vh',
                        disableClose: false,
                        autoFocus: false
                    });
                },
                error: (err: any) => {
                    this.openingUnlinkedIds.delete(row.id);
                    this.handleUnlinkedError(err, 'Could not load the result.');
                }
            });
    }

    /**
     * Downloads the server-rendered report PDF.
     *
     * Mirrors the lab-order download exactly (`PatientLabService.downloadLabResultsAsPdf` ->
     * `fileDownloadService.download`): the SERVER renders the PDF and returns a blob. No jsPDF.
     * Specimen numbers can contain colons, which are illegal in a file name, so the name is
     * built through `unlinkedPdfFileName`.
     */
    downloadUnlinkedResultPdf(event: Event, row: UnlinkedResultDto): void {
        event.stopPropagation();
        if (this.isUnlinkedDownloading(row)) {
            return;
        }
        const groupId = this.unlinkedGroupId(row);
        if (!groupId) {
            this.showToastMessage('No group selected.');
            return;
        }

        this.downloadingUnlinkedIds.add(row.id);
        this.resultsService.downloadUnlinkedResultPdf(groupId, row.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: async (blob: Blob) => {
                    await this.fileDownloadService.download(blob, unlinkedPdfFileName(row.specimenNumber, row.id));
                    this.downloadingUnlinkedIds.delete(row.id);
                    this.showToastMessage('Lab results PDF downloaded successfully');
                },
                error: (err: any) => {
                    this.downloadingUnlinkedIds.delete(row.id);
                    // A blob request returns a Blob for errors too, so there is no readable
                    // { error, message } body to quote.
                    this.showToastMessage(unlinkedBlobErrorMessage(err, 'Failed to download lab results PDF'));
                }
            });
    }

    /** The raw payload is PHI — fetched on demand, never with the list. */
    viewUnlinkedPayload(event: Event, row: UnlinkedResultDto): void {
        event.stopPropagation();
        const groupId = this.unlinkedGroupId(row);
        if (!groupId) {
            this.showToastMessage('No group selected.');
            return;
        }

        this.resultsService.getUnlinkedPayload(groupId, row.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    if (!res?.success || !res?.data) {
                        this.showToastMessage('Could not load the payload.');
                        return;
                    }
                    const dialogData: UnlinkedResultPayloadDialogData = {
                        title: `${this.unlinkedPatientName(row)} · specimen ${row.specimenNumber || 'unknown'}`,
                        rawPayload: res.data.rawPayload || ''
                    };
                    this.dialog.open(UnlinkedResultPayloadDialogComponent, {
                        width: '820px',
                        maxWidth: '95vw',
                        maxHeight: '90vh',
                        autoFocus: false,
                        data: dialogData
                    });
                },
                error: (err: any) => {
                    this.handleUnlinkedError(err, 'Could not load the payload.');
                }
            });
    }

    /**
     * Surfaces the server's refusal codes properly. A 409 means another session already
     * linked or dismissed this row, so this tab's copy is stale: reload rather than leaving
     * a row on screen that no longer exists.
     */
    private handleUnlinkedError(err: any, fallback: string): void {
        this.showToastMessage(unlinkedErrorMessage(err, fallback));
        if (isUnlinkedConflict(err)) {
            this.resultsService.loadUnlinked()
                .pipe(takeUntil(this.destroy$))
                .subscribe();
        }
    }

    private precomputeDisplayData(order: any): any {
        const patient = order.Patient || order.patientSnapshot || {};
        return {
            ...order,
            diagnosisString: this.buildDiagnosisString(patient),
            testNames: this.getTestNames(order.labTests),
            providerLogo: this.getProviderLogo(order.labProvider)
        };
    }

    onTabChange(index: number) {
        this.selectedTabIndex = index;
    }

    buildDiagnosisString(patient: any): string {
        if (!patient) return '';
        const { diagnosis2, diagnosis3, diagnosis4, diagnosis5 } = patient;
        const diagnoses = [diagnosis2, diagnosis3, diagnosis4, diagnosis5].filter(Boolean);
        const diagnosisString = diagnoses.join(', ');
        return diagnosisString.length > 70 ? `${diagnosisString.substring(0, 70)}...` : diagnosisString;
    }

    getTestNames(labTests: any): string {
        if (!labTests || !Array.isArray(labTests)) return '';
        return labTests.map((test: any) => test.name || test).join(', ');
    }

    private groupAndSort(data: any[]) {
        const grouped = _.groupBy(data, item => {
            const date = item.resultDate || item.orderDate || item.createdAt;
            return date ? moment(date).format('YYYY-MM-DD') : moment(item.createdAt).format('YYYY-MM-DD');
        });

        return _.chain(grouped)
            .map((value, key) => ({
                date: key,
                items: _.orderBy(value, ['Patient?.surname', 'Patient?.name'], ['asc', 'asc'])
            }))
            .orderBy(group => group.date, 'desc')
            .value();
    }

    navigateToPatientFile(patientId: string, orderId?: string) {
        this.patientService.getPatientById(patientId)
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe((result: any) => {
                if (orderId) {
                    // Navigate to patient file with lab order in view
                    this.router.navigate(['/patients', {
                        outlets: {
                            patient: ['file', patientId]
                        }
                    }], { queryParams: { labOrder: orderId } });
                } else {
                    this.router.navigate(['/patients', {
                        outlets: {
                            patient: ['file', patientId],
                        }
                    }]);
                }
            }, (error: any) => {
                console.error(error);
                this.showToastMessage('There was an error loading the patient file. Please try again.');
            });
    }

    onViewIconClick(event: Event, order: any) {
        event.stopPropagation(); // Prevent navigation to patient file
        
        const patientId = order.patientId || order.Patient?.id;
        if (!patientId) {
            this.showToastMessage('Patient information not available');
            return;
        }

        // If completed or cancelled, open results dialog
        if (order.status === 'completed' || order.status === 'cancelled') {
            this.viewLabResults(order, patientId);
        } else {
            // Otherwise, edit the lab order
            this.editLabOrder(order);
        }
    }

    viewLabResults(order: any, patientId: string): void {
        // Load patient data first
        this.patientService.getPatientById(patientId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (patient: any) => {
                    const dialogData: LabResultsDialogData = {
                        labOrder: order,
                        patient: patient,
                        // `order.vendorDoctors` is already on the row; only the code to suppress
                        // has to be handed over, because it lives on the list ENVELOPE.
                        vendorDoctorPlatformCode: this.vendorDoctorPlatformCode
                    };

                    this.dialog.open(LabResultsDialogComponent, {
                        data: dialogData,
                        width: '90vw',
                        /* maxWidth: '900px', */
                        height: '90vh',
                        disableClose: false,
                        autoFocus: false
                    });
                },
                error: (error: any) => {
                    console.error('Error loading patient:', error);
                    this.showToastMessage('Error loading patient information');
                }
            });
    }

    editLabOrder(order: any, event?: Event): void {
        if (event) {
            event.stopPropagation(); // Prevent navigation to patient file
        }
        
        const patientId = order.patientId || order.Patient?.id;
        if (!patientId) {
            this.showToastMessage('Patient information not available');
            return;
        }

        // Load patient first (this sets it as current patient)
        this.patientService.getPatientById(patientId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (patient: any) => {
                    // Set editing lab order in service (following task-list pattern)
                    this.patientLabService.setEditingLabOrder(order);
                    // Navigate to edit route
                    this.router.navigate(['/patients', {
                        outlets: {
                            patient: ['edit-lab-order', order.id]
                        }
                    }]);
                },
                error: (error: any) => {
                    console.error('Error loading patient:', error);
                    this.showToastMessage('There was an error loading the patient for editing. Please try again.');
                }
            });
    }

    formatDate(date: string | Date): string {
        if (!date) return '';
        return moment(date).format('YYYY-MM-DD');
    }

    // Get provider logo based on lab provider
    getProviderLogo(labProvider: string): string {
        if (!labProvider) return '';

        const provider = labProvider.toLowerCase();
        if (provider.includes('lancet')) {
            return this.lancetLogo;
        }

        return '';
    }
}

